import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import SEO from '../components/shared/SEO';
import { tools, siteConfig } from '../data/mock';

const ToolsPage = () => {
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://summarygpt.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Tools',
        item: 'https://summarygpt.com/tools',
      },
    ],
  };

  return (
    <Layout>
      <SEO
        title="Our Tools"
        description={`AI tools built to solve one problem well — including ${tools.map((t) => t.name).join(', ')}.`}
        path="/tools"
        schema={breadcrumbSchema}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 sgpt-glow opacity-60 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors mb-10 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            What we build.
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 max-w-2xl leading-relaxed">
            Each tool solves one specific problem — start to finish.
          </p>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-5xl" />

      {/* Tools */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 sgpt-glow-subtle opacity-40 pointer-events-none" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {tools.map((tool, index) => {
              const isLive = tool.status === 'live';
              
              return (
                <div key={tool.id} className="relative">
                  {/* Tool number */}
                  <div className="text-8xl md:text-9xl font-bold text-white/5 absolute -top-6 -left-2 select-none pointer-events-none">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  
                  <div className="relative glass-card rounded-2xl p-8 md:p-12">
                    <div className="grid lg:grid-cols-5 gap-10">
                      {/* Main content - 3 cols */}
                      <div className="lg:col-span-3">
                        <div className="flex items-center gap-4 mb-6">
                          <h2 className="text-3xl md:text-4xl font-bold text-white">
                            {tool.name}
                          </h2>
                          {isLive ? (
                            <span className="px-3 py-1 text-xs font-semibold bg-emerald-500/15 text-emerald-400 rounded-full border border-emerald-500/25">
                              Live
                            </span>
                          ) : (
                            <span className="px-3 py-1 text-xs font-semibold bg-amber-500/15 text-amber-400 rounded-full border border-amber-500/25">
                              Coming Soon
                            </span>
                          )}
                        </div>
                        
                        <p className="text-2xl text-amber-400 font-medium mb-4">
                          {tool.tagline}
                        </p>
                        
                        <p className="text-lg text-slate-400 mb-6 leading-relaxed">
                          {tool.description}
                        </p>

                        <p className="text-slate-500 italic mb-8">
                          {tool.highlight}
                        </p>

                        <div className="flex flex-wrap gap-4">
                          {isLive && tool.appUrl ? (
                            <a href={tool.appUrl} target="_blank" rel="noopener noreferrer">
                              <Button className="h-12 px-8 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-900 font-semibold shadow-lg shadow-amber-500/15 hover:shadow-amber-500/25 transition-all duration-300 border-0 gap-2 rounded-xl">
                                Try {tool.name}
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            </a>
                          ) : (
                            <a href={`mailto:hello@summarygpt.com?subject=${tool.name}%20Early%20Access`}>
                              <Button className="h-12 px-8 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-900 font-semibold shadow-lg shadow-amber-500/15 hover:shadow-amber-500/25 transition-all duration-300 border-0 gap-2 rounded-xl">
                                Get Early Access
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            </a>
                          )}
                          <Link to={tool.internalUrl}>
                            <Button variant="outline" className="h-12 px-8 bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white hover:border-white/20 transition-all duration-300 gap-2 rounded-xl">
                              Full details
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>

                      {/* Benefits - 2 cols */}
                      <div className="lg:col-span-2">
                        <div className="rounded-xl bg-black/20 border border-white/5 p-6 h-full">
                          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-5">What you get</p>
                          <ul className="space-y-4">
                            {tool.whatYouGet.map((item, i) => (
                              <li key={i} className="flex items-start gap-3 text-slate-300">
                                <Check className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ToolsPage;
